import './TextBase.scss';
import { Component } from '@components/Component/Component.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { zoomHandler } from '@components/Utility/index.js';
import { ENV } from '@components/Environment.js';
import { combineLatestWith, debounceTime, startWith, takeUntil } from 'rxjs/operators';
/**
 * `Hint` component is for adding text to a form without any input or anything. Often placed below a grid or set of inputs to provide some context or instruction.
 */
export class TextBase extends Component {
    constructor() {
        super(...arguments);
        this.componentName = 'TextBase';
        this.rootElementType = 'div';
        /**
         * The HTML text element type
         * @protected
         */
        this._textElementType = 'p';
        this._textElementAttributes = {};
        this._fontWeight = '';
        this._fontSize = '';
        this._textColor = '';
    }
    get zoomTemplate() { return this._zoomTemplate || ''; }
    set zoomTemplate(value) {
        this._propertySet('zoomTemplate', value);
        this.renderZoom();
    }
    get zoomConditions() { return this._zoomConditions; }
    set zoomConditions(value) {
        this._propertySet('zoomConditions', value);
        this.renderZoom();
    }
    get textElementType() { return this._textElementType; }
    set textElementType(textElementType) {
        this._propertySet('textElementType', textElementType);
        if (this.drawn) {
            this.renderTextElement();
        }
    }
    get textElementAttributes() { return this._textElementAttributes; }
    set textElementAttributes(textElementAttributes) {
        this._propertySet('textElementAttributes', textElementAttributes);
        if (this.drawn) {
            this.renderTextElement();
        }
    }
    get fontWeight() { return this._fontWeight; }
    set fontWeight(value) {
        this._propertySet('fontWeight', value);
        if (this.drawn) {
            this.renderFontWeight();
        }
    }
    /** @deprecated Use fontWeight instead. Retained for backward compatibility with saved forms. */
    get bold() { return this._fontWeight === 'font-bold'; }
    set bold(value) {
        if (typeof value === 'boolean') {
            this.fontWeight = value ? 'font-bold' : '';
        }
        else {
            this.fontWeight = value || '';
        }
    }
    get fontSize() { return this._fontSize; }
    set fontSize(value) {
        this._propertySet('fontSize', value);
        if (this.drawn) {
            this.renderFontSize();
        }
    }
    get textColor() { return this._textColor; }
    set textColor(value) {
        this._propertySet('textColor', value);
        if (this.drawn) {
            this.renderTextColor();
        }
    }
    get align() { return this._align; }
    set align(align) {
        this._propertySet('align', align);
        if (this.drawn) {
            this.renderAlign();
        }
    }
    get caption() { return this._caption || ''; }
    set caption(value) {
        this._propertySet('caption', value);
        this.renderCaption();
    }
    get tooltip() { return this._tooltip || ''; }
    set tooltip(value) {
        if ((value || '') != (this._tooltip || '')) {
            this._propertySet('tooltip', value);
            this.renderTooltip();
        }
    }
    captionTypesSet() {
        this.captionTypes = {
            caption: 'caption',
            tooltip: 'tooltip'
        };
    }
    draw() {
        super.draw();
        if (this._fontWeight) {
            this.renderFontWeight();
        }
        if (this._fontSize) {
            this.renderFontSize();
        }
        if (this._textColor) {
            this.renderTextColor();
        }
        if (this._align) {
            this.renderAlign();
        }
        // if (this.caption) {
        // 	this.renderCaption();
        // }
    }
    unlinkRecord(keepState = false) {
        if (this.recordChangeSubscription) {
            this.recordChangeSubscription.unsubscribe();
            this.recordChangeSubscription = undefined;
        }
        super.unlinkRecord(keepState);
    }
    linkRecord(record) {
        super.linkRecord(record);
        if (this.recordChangeSubscription) {
            this.recordChangeSubscription.unsubscribe();
        }
        this.recordChangeSubscription = record.load$.pipe(combineLatestWith(record.allChangesGet$().pipe(startWith(undefined))), debounceTime(100), takeUntil(this.destroy$))
            .subscribe(() => {
            this.handleRecordChanges();
        });
    }
    handleRecordChanges() {
        this.renderZoom();
    }
    renderZoom() {
        if (!this.textElement) {
            return;
        }
        if (this.zoomEventId) {
            this.unRegisterEvent(this.zoomEventId);
        }
        const conditions = this.zoomConditions;
        const template = this.zoomTemplate;
        const conditionsMgr = DIContainer.getInstance('ConditionsManager');
        let templateReady = !!template;
        if (templateReady && this.record) {
            // ensure we have all the tokens that we need
            const globalTokenRegex = /{{(.*?)}}/g;
            let match;
            while ((match = globalTokenRegex.exec(template)) !== null) {
                const tag = match[1];
                const [tokenName] = tag.split(':');
                if (!this.record.get(tokenName) && !template.startsWith('/form/sqlview')) {
                    templateReady = false;
                }
            }
        }
        if (templateReady && (!conditions || conditionsMgr.validateConditions(conditions))) {
            this.textElement.classList.add('link');
            this.zoomEventId = this.registerEvent('click', this.textElement, (event) => {
                this.trigger('click', {
                    'defaultEventHandler': () => {
                        let target = '_top';
                        if (ENV('appName') === 'formbuilder') {
                            target = '_blank';
                        }
                        zoomHandler(template, {
                            target: target,
                            data: this.record?.data,
                            initiator: this,
                            event
                        }).subscribe();
                    }
                });
            });
        }
        else {
            this.textElement.classList.remove('link');
        }
    }
    renderTextElement() {
        if (this.textElement && this.rootElement.contains(this.textElement)) {
            this.rootElement.removeChild(this.textElement);
        }
        this.textElement = document.createElement(this._textElementType);
        for (const [attr, value] of Object.entries(this.textElementAttributes)) {
            this.textElement.setAttribute(attr, value);
        }
        this.renderCaption();
        if (this._tooltip) {
            this.renderTooltip();
        }
        if (this.zoomTemplate) {
            this.renderZoom();
        }
        this.rootElement.appendChild(this.textElement);
    }
    renderCaption() {
        if (this.textElement) {
            // Clear the current text content
            this.textElement.textContent = '';
            // Split the caption by new lines
            const lines = (this._caption || '').split('\n');
            // Append each line as a text node with a line break
            lines.forEach((line, index) => {
                if (index > 0) {
                    this.textElement.appendChild(document.createElement('br'));
                }
                this.textElement.appendChild(document.createTextNode(line));
            });
        }
    }
    renderTooltip() {
        if (this.textElement) {
            this.registerTooltip(this.textElement, {
                tooltip: this.tooltip
            });
        }
    }
    renderFontWeight() {
        TextBase.FONT_WEIGHT_CLASSES.forEach(cls => this.rootElement?.classList.remove(cls));
        if (this._fontWeight && TextBase.FONT_WEIGHT_CLASSES.includes(this._fontWeight)) {
            this.rootElement?.classList.add(this._fontWeight);
        }
    }
    renderFontSize() {
        TextBase.FONT_SIZE_CLASSES.forEach(cls => this.rootElement?.classList.remove(cls));
        if (this._fontSize && TextBase.FONT_SIZE_CLASSES.includes(this._fontSize)) {
            this.rootElement?.classList.add(this._fontSize);
        }
    }
    renderTextColor() {
        TextBase.TEXT_COLOR_CLASSES.forEach(cls => this.rootElement?.classList.remove(cls));
        if (this._textColor && TextBase.TEXT_COLOR_CLASSES.includes(this._textColor)) {
            this.rootElement?.classList.add(this._textColor);
        }
    }
    renderAlign() {
        this.rootElement?.classList.remove('left');
        this.rootElement?.classList.remove('center');
        this.rootElement?.classList.remove('right');
        if (this._align) {
            this.rootElement?.classList.add(this._align);
        }
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        properties.fontWeight = ({
            id: 'fontWeight',
            value: this._fontWeight,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'Default' },
                    { value: 'font-normal', display: 'Normal (400)' },
                    { value: 'font-medium', display: 'Medium (500)' },
                    { value: 'font-semibold', display: 'Semibold (600)' },
                    { value: 'font-bold', display: 'Bold (700)' }
                ]
            },
            captionKey: 'formbuilder_textbase_fontweight',
            propertyGroup: '',
            saveFlag: !!this._fontWeight
        });
        properties.fontSize = ({
            id: 'fontSize',
            value: this._fontSize,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'Default' },
                    { value: 'text-xxs', display: 'XXS (10px)' },
                    { value: 'text-xs', display: 'XS (12px)' },
                    { value: 'text-sm', display: 'SM (14px)' },
                    { value: 'text-md', display: 'MD (16px)' },
                    { value: 'text-lg', display: 'LG (18px)' },
                    { value: 'text-xl', display: 'XL (20px)' },
                    { value: 'text-2xl', display: '2XL (24px)' },
                    { value: 'text-3xl', display: '3XL (30px)' },
                    { value: 'display-xs', display: 'Display XS (24px)' },
                    { value: 'display-sm', display: 'Display SM (30px)' },
                    { value: 'display-md', display: 'Display MD (36px)' },
                    { value: 'display-lg', display: 'Display LG (48px)' },
                    { value: 'display-xl', display: 'Display XL (60px)' },
                    { value: 'display-2xl', display: 'Display 2XL (72px)' }
                ]
            },
            captionKey: 'formbuilder_textbase_fontsize',
            propertyGroup: '',
            saveFlag: !!this._fontSize
        });
        properties.textColor = ({
            id: 'textColor',
            value: this._textColor,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'Default' },
                    { value: 'text-color-primary', display: 'Primary' },
                    { value: 'text-color-secondary', display: 'Secondary' },
                    { value: 'text-color-tertiary', display: 'Tertiary' },
                    { value: 'text-color-link', display: 'Link' }
                ]
            },
            captionKey: 'formbuilder_textbase_textcolor',
            propertyGroup: '',
            saveFlag: !!this._textColor
        });
        properties.align = ({
            id: 'align',
            value: this._align,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: 'left', display: 'Left' },
                    { value: 'center', display: 'Center' },
                    { value: 'right', display: 'Right' }
                ]
            },
            captionKey: 'formbuilder_textbase_align',
            propertyGroup: '',
            saveFlag: () => !!this._align
        });
        properties.textElementType = ({
            id: 'textElementType',
            value: this._textElementType,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: 'p', display: 'Paragraph - p' },
                    { value: 'h1', display: 'Heading 1 - h1' },
                    { value: 'h2', display: 'Heading 2 - h2' },
                    { value: 'h3', display: 'Heading 3 - h3' },
                    { value: 'h4', display: 'Heading 4 - h4' },
                    { value: 'h5', display: 'Heading 5 - h5' },
                    { value: 'h6', display: 'Heading 6 - h6' },
                    { value: 'label', display: 'Label - label' },
                    // {value: 'a', display: 'Anchor - a'},
                    { value: 'span', display: 'Span' },
                    { value: 'div', display: 'Division - div' },
                    { value: 'em', display: 'Emphasis - em' },
                    { value: 'strong', display: 'Strong' },
                    { value: 'blockquote', display: 'Blockquote' },
                    { value: 'pre', display: 'Preformatted Text - pre' },
                    { value: 'code', display: 'Code' },
                    // {value: 'ul', display: 'Unordered List - ul'},
                    // {value: 'ol', display: 'Ordered List - ol'},
                    // {value: 'li', display: 'List Item - li'},
                    // {value: 'dl', display: 'Definition List - dl'},
                    // {value: 'dt', display: 'Definition Term - dt'},
                    // {value: 'dd', display: 'Definition Description - dd'},
                    { value: 'small', display: 'Small' },
                    { value: 'cite', display: 'Citation - cite' },
                    { value: 'abbr', display: 'Abbreviation - abbr' },
                    { value: 'address', display: 'Address - address' }
                ]
            },
            captionKey: 'formbuilder_textbase_textelementtype',
            propertyGroup: '',
            saveFlag: this._textElementType !== 'p' // if its not the default type, we need to save it
        });
        properties.zoomTemplate = {
            id: 'zoomTemplate',
            value: this.zoomTemplate,
            componentType: 'Input',
            componentProperties: {},
            captionKey: 'formbuilder_zoomtemplate',
            propertyGroup: 'caption',
            propertyIndex: 7,
            saveFlag: !!this.zoomTemplate
        };
        properties.zoomConditions = {
            id: 'zoomConditions',
            value: this.zoomConditions,
            componentType: 'ConditionsEditor',
            componentProperties: {
                labelPosition: 'top'
            },
            captionKey: 'formbuilder_zoomconditions',
            propertyGroup: 'caption',
            propertyIndex: 8,
            saveFlag: !!this.zoomConditions
        };
        return properties;
    }
    /**
     * Returns an empty template as the hint is built programmatically
     * @returns {string} - An empty string
     */
    template() {
        this.renderTextElement();
        this.rootElement.classList.add('s360TextBase');
        return '';
    }
}
TextBase.VERSION = '$Revision: 112048 $';
TextBase.FONT_WEIGHT_CLASSES = ['font-normal', 'font-medium', 'font-semibold', 'font-bold'];
TextBase.FONT_SIZE_CLASSES = [
    'text-xxs', 'text-xs', 'text-sm', 'text-md', 'text-lg', 'text-xl',
    'text-2xl', 'text-3xl',
    'display-xs', 'display-sm', 'display-md', 'display-lg', 'display-xl', 'display-2xl'
];
TextBase.TEXT_COLOR_CLASSES = ['text-color-primary', 'text-color-secondary', 'text-color-tertiary', 'text-color-link'];
